/**
 * PATH: packages/api/src/observability.ts
 * FILE_ID: API-OBS-SEAM-001
 * PURPOSE: Seam observability for TanStack Query — one MutationCache tap +
 *          one QueryCache tap per QueryClient, so every write (fire → settle
 *          → rollback) and every slow/failed read is captured with timing,
 *          WITHOUT any per-call-site instrumentation.
 * WHY: FRAME_TEMPLATE guardrail #6 (docs/architecture/FRAME_TEMPLATE.md,
 *      AIHUB). Before this, a failed kanban mutation rolled back silently —
 *      error visibility in prod was zero and MTTR was "wait for the user to
 *      report it". Payload policy (operator, 2026-07-04): FULL payloads in
 *      these internal logs; redaction happens only at third-party export
 *      boundaries, never at capture time.
 * FLOW:
 *   mutation/query lifecycle event → classify (fire/settle/error/slow)
 *     → ring buffer (window.__QT_OBS__, capped) → console.warn on breakage
 * DEPENDENCIES:
 *   - @tanstack/react-query QueryClient: the caches being tapped
 * RELATED:
 *   - API-OBS-SEAM-001 consumers: provider.tsx createQueryClient(); tenant
 *     mains bridge events into @quotech/ui recordDiagnosticEvent (the
 *     EXISTING prod ingest pipeline — reuse gate: this module deliberately
 *     does NOT duplicate the diagnostics buffer/ingest, it only adds the
 *     TanStack seam that pipeline was missing) via setSeamObservabilitySink.
 */

import type { QueryClient } from '@tanstack/react-query'

export interface ObsEvent {
  /** epoch ms */
  ts: number
  kind:
    | 'mutation:fire'
    | 'mutation:success'
    | 'mutation:error'
    | 'query:fetch'
    | 'query:error'
    | 'query:slow'
  /** mutationKey / queryKey, JSON-ish */
  key: unknown
  /** correlation id: stable per mutation instance across fire→settle */
  actionId?: string
  durationMs?: number
  /** FULL variables/error payload — internal logs, no capture-time redaction */
  payload?: unknown
  error?: string
}

export interface ObsState {
  events: ObsEvent[]
  counters: {
    mutations: number
    mutationErrors: number
    queryErrors: number
    slowQueries: number
    /** TanStack QueryCache fetch starts. The job-details request count. */
    queries: number
    /** HTTP requests that passed through createApiClient. */
    httpRequests: number
  }
  /**
   * Mirror of the module's currentActionId, published on the window state so
   * clients OUTSIDE this package's dependency graph (the tenant-shared fetch
   * wrapper) can stamp X-Correlation-ID without a package cycle.
   */
  currentActionId?: string | null
}

/** Reads above this are logged as slow. Generous: catches real degradation, not jitter. */
const SLOW_QUERY_MS = 3000
/** Ring buffer cap — enough to reconstruct an incident, small enough to never matter. */
const MAX_EVENTS = 500

declare global {
  interface Window {
    __QT_OBS__?: ObsState
  }
}

function emptyCounters(): ObsState['counters'] {
  return {
    mutations: 0,
    mutationErrors: 0,
    queryErrors: 0,
    slowQueries: 0,
    queries: 0,
    httpRequests: 0,
  }
}

function obsState(): ObsState {
  if (typeof window === 'undefined') {
    return { events: [], counters: emptyCounters() }
  }
  if (!window.__QT_OBS__) {
    window.__QT_OBS__ = {
      events: [],
      counters: emptyCounters(),
    }
  }
  const state = window.__QT_OBS__
  state.counters.queries ??= 0
  state.counters.httpRequests ??= 0
  return state
}

/**
 * Count one HTTP request that already went through `@quotech/api`.
 * Counter only — the ring stays reserved for lifecycle events.
 */
export function recordHttpRequest(): void {
  obsState().counters.httpRequests += 1
}

/**
 * Optional downstream sink for BREAKAGE events (errors + slow queries).
 * Tenant mains point this at @quotech/ui's recordDiagnosticEvent so seam
 * failures ride the existing prod ingest pipeline (ring buffer → batched
 * POST → audit/views_client_diagnostics.py). A callback keeps the package
 * dependency direction clean: ui→api, never api→ui.
 */
type ObsSink = (event: ObsEvent) => void
let breakageSink: ObsSink | null = null

export function setSeamObservabilitySink(sink: ObsSink | null): void {
  breakageSink = sink
}

/**
 * FE↔BE correlation: the actionId of the most recently FIRED, still-pending
 * mutation. The axios client reads this to stamp X-Correlation-ID on write
 * requests, so the backend's CorrelationIdMiddleware adopts the SAME id the
 * seam tap logged — one id joins the FE ring-buffer event, the Django
 * structured logs, and the audit.ServerTimingSample row.
 *
 * Known limit (accepted): with several mutations genuinely in flight at once
 * the last-fired id wins for requests issued in that window. Mutations fire
 * their HTTP request synchronously after 'pending', so in practice the pair
 * is correct; the alternative (threading ids through every mutationFn) would
 * re-introduce per-call-site instrumentation this module exists to avoid.
 */
let currentActionId: string | null = null
let pendingMutations = 0

function setCurrentActionId(next: string | null): void {
  currentActionId = next
  if (typeof window !== 'undefined') {
    obsState().currentActionId = next
  }
}

export function getCurrentActionId(): string | null {
  return currentActionId
}

/**
 * Manual correlation scope for actions that are NOT TanStack mutations
 * (e.g. the email reader's action runner, whose transports call the
 * fetch-based email client directly). Sets the id for the synchronous
 * portion of `fn` — long enough for any HTTP request fired in the same
 * tick to pick it up — then restores the previous id.
 */
export function runWithActionId<T>(actionId: string, fn: () => T): T {
  const previous = currentActionId
  setCurrentActionId(actionId)
  try {
    return fn()
  } finally {
    setCurrentActionId(previous)
  }
}

function record(state: ObsState, event: ObsEvent): void {
  state.events.push(event)
  if (state.events.length > MAX_EVENTS) state.events.splice(0, state.events.length - MAX_EVENTS)
  // Breakage is loud; healthy traffic only lands in the ring buffer.
  const isBreakage =
    event.kind === 'mutation:error' || event.kind === 'query:error' || event.kind === 'query:slow'
  if (event.kind === 'query:slow') {
    console.warn('[obs]', event.kind, event.key, `${event.durationMs}ms`)
  } else if (isBreakage) {
    console.warn('[obs]', event.kind, event.key, event.error, event.payload ?? '')
  }
  if (isBreakage && breakageSink) {
    try {
      breakageSink(event)
    } catch {
      // A broken sink must never break the app it's observing.
    }
  }
}

/**
 * Attach the two seam taps to a QueryClient. Idempotent per client instance
 * (a WeakSet guards double-subscription under HMR/StrictMode re-runs).
 * Returns an unsubscribe function (used by tests; apps never need it).
 */
const attached = new WeakSet<QueryClient>()

export function attachSeamObservability(client: QueryClient): () => void {
  if (attached.has(client)) return () => {}
  attached.add(client)

  const state = obsState()
  // mutationId → { start, actionId } for fire→settle duration + correlation.
  const inflight = new Map<number, { start: number; actionId: string }>()
  let seq = 0
  const session = Math.random().toString(36).slice(2, 8)

  const unsubMutations = client.getMutationCache().subscribe((event) => {
    const mutation = 'mutation' in event ? event.mutation : undefined
    if (!mutation) return
    const key = mutation.options.mutationKey ?? '(no mutationKey)'
    const status = mutation.state.status

    if (event.type === 'updated' && status === 'pending' && !inflight.has(mutation.mutationId)) {
      const actionId = `act-${session}-${++seq}`
      inflight.set(mutation.mutationId, { start: Date.now(), actionId })
      setCurrentActionId(actionId)
      pendingMutations += 1
      state.counters.mutations += 1
      record(state, {
        ts: Date.now(),
        kind: 'mutation:fire',
        key,
        actionId,
        payload: mutation.state.variables,
      })
      return
    }

    if (event.type === 'updated' && (status === 'success' || status === 'error')) {
      const flight = inflight.get(mutation.mutationId)
      inflight.delete(mutation.mutationId)
      if (flight) {
        pendingMutations = Math.max(0, pendingMutations - 1)
        if (pendingMutations === 0) setCurrentActionId(null)
      }
      const durationMs = flight ? Date.now() - flight.start : undefined
      if (status === 'error') state.counters.mutationErrors += 1
      record(state, {
        ts: Date.now(),
        kind: status === 'error' ? 'mutation:error' : 'mutation:success',
        key,
        actionId: flight?.actionId,
        durationMs,
        payload: mutation.state.variables,
        error: status === 'error' ? String(mutation.state.error) : undefined,
      })
    }
  })

  // queryHash → fetch start. Durations are per-fetch, not per-mount.
  const fetching = new Map<string, number>()

  const unsubQueries = client.getQueryCache().subscribe((event) => {
    const query = 'query' in event ? event.query : undefined
    if (!query || event.type !== 'updated') return
    const action = 'action' in event ? (event.action as { type?: string }) : undefined

    if (action?.type === 'fetch') {
      fetching.set(query.queryHash, Date.now())
      state.counters.queries += 1
      record(state, {
        ts: Date.now(),
        kind: 'query:fetch',
        key: query.queryKey,
      })
      return
    }

    if (action?.type === 'success' || action?.type === 'error') {
      const start = fetching.get(query.queryHash)
      fetching.delete(query.queryHash)
      const durationMs = start ? Date.now() - start : undefined

      if (action.type === 'error') {
        state.counters.queryErrors += 1
        record(state, {
          ts: Date.now(),
          kind: 'query:error',
          key: query.queryKey,
          durationMs,
          error: String(query.state.error),
        })
      } else if (durationMs !== undefined && durationMs > SLOW_QUERY_MS) {
        state.counters.slowQueries += 1
        record(state, { ts: Date.now(), kind: 'query:slow', key: query.queryKey, durationMs })
      }
    }
  })

  return () => {
    unsubMutations()
    unsubQueries()
    attached.delete(client)
  }
}
